import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from './services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setError(null);
  };

  useEffect(() => {
    const bootstrapSession = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      if (!token || !savedUser) {
        setLoading(false);
        return;
      }

      try {
        const parsed = JSON.parse(savedUser);
        if (parsed?.role !== 'student') {
          logout();
          setLoading(false);
          return;
        }

        setUser(parsed);
        const response = await authAPI.getCurrentStudent();
        const studentUser = { ...response.data, role: 'student' };
        setUser(studentUser);
        localStorage.setItem('user', JSON.stringify(studentUser));
      } catch {
        logout();
      } finally {
        setLoading(false);
      }
    };

    bootstrapSession();
  }, []);

  const login = async ({ email, password }) => {
    try {
      setError(null);
      const response = await authAPI.login({ email, password });
      const { access_token, student } = response.data;

      localStorage.setItem('token', access_token);
      const studentUser = { ...student, role: 'student' };
      setUser(studentUser);
      localStorage.setItem('user', JSON.stringify(studentUser));

      return { success: true, role: 'student' };
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      await authAPI.register(userData);
      return { success: true, needsVerification: true, email: userData.email };
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getCurrentStudent();
      const studentUser = { ...response.data, role: 'student' };
      setUser(studentUser);
      localStorage.setItem('user', JSON.stringify(studentUser));
      return studentUser;
    } catch {
      return null;
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    isStudent: user?.role === 'student',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
